import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { byLastName, isPaidOrSetUp, paymentMethodOf, watchCustomers, watchInvoices } from '../lib/data.js'
import { markInvoicePaidManual } from '../lib/stripe.js'
import { currentCycleInvoice, paymentBadgeState } from '../lib/paymentBadge.js'
import { fmtDateTime, fmtMoney, statusPillClass } from '../lib/format.js'
import SmsComposerModal from '../admin/SmsComposerModal.jsx'
import BulkSmsModal from '../admin/BulkSmsModal.jsx'

// Strict (customerType, billingCycle) partitioning. Every customer falls
// into exactly one bucket:
//
//   customerType = 'program' + billingCycle = '1st'   → YN AL Customers 1st
//   customerType = 'program' + billingCycle = '15th'  → YN AL Customers 15th
//   customerType = 'invoice' + billingCycle = '1st'   → YN Customers 1st
//   customerType = 'invoice' + billingCycle = '15th'  → YN Customers 15th
//
// AL ('program') never appears in YN ('invoice') and vice versa.
// 1st-cycle never appears in 15th-cycle. The split mixes auto-draft and
// invoice customers inside each cycle bucket — payment method shows in
// the per-row METHOD column, not as a tab.
export default function Customers({ customerType, billingCycle }) {
  const navigate = useNavigate()
  const [customers, setCustomers] = useState({})
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [methodFilter, setMethodFilter] = useState('all')
  const [minAmount, setMinAmount] = useState('')
  const [smsTarget, setSmsTarget] = useState(null)    // { id, customer } for single SMS modal
  const [showBulkSms, setShowBulkSms] = useState(false)
  const [markingId, setMarkingId] = useState(null)    // invoice id currently being recorded paid
  const [markFlash, setMarkFlash] = useState(null)    // { kind, msg } — errors only; success is silent (row goes green live)

  const [invoices, setInvoices] = useState({})
  useEffect(() => watchCustomers(setCustomers), [])
  useEffect(() => watchInvoices(setInvoices), [])

  // For each customer, pick their CURRENT billing-cycle invoice (not merely the
  // most-recent by createdAt — that lets a prior month's paid invoice linger as
  // "Paid"). If a customer has invoices but none in the current cycle, we mark
  // them Unpaid (the monthly reset). Customers with no invoice at all stay off
  // the badge entirely (clean cell for stub / no-bill records).
  const currentInvoiceByCustomer = useMemo(() => {
    const byCust = {}
    // Attach each invoice's push-id — watchInvoices hands us the raw RTDB map
    // whose values omit their own key, and the Mark Check Paid control needs
    // inv.id to target the invoice (else it reads "no invoice this cycle").
    for (const [iid, inv] of Object.entries(invoices || {})) {
      if (!inv?.customerId) continue
      ;(byCust[inv.customerId] = byCust[inv.customerId] || []).push({ id: iid, ...inv })
    }
    const m = {}
    for (const [cid, list] of Object.entries(byCust)) {
      // null current-cycle invoice but has history → synthetic Unpaid (reset).
      m[cid] = currentCycleInvoice(customers[cid], list) || { status: 'unpaid', __noCurrentCycle: true }
    }
    return m
  }, [invoices, customers])

  // Apply the scoping props BEFORE the search/status/method/minAmount
  // filters run, so the headline count and the rendered list both reflect
  // the same slice.
  const scopedCustomers = useMemo(() => {
    const out = {}
    for (const [id, c] of Object.entries(customers)) {
      // Strict customerType match — AL and YN never mix.
      if (customerType && c.customerType !== customerType) continue
      // Strict billingCycle match — 1st and 15th never mix.
      if (billingCycle && c.billingCycle !== billingCycle) continue
      out[id] = c
    }
    return out
  }, [customers, customerType, billingCycle])

  // Total customer count — derived from the live customers map, so it
  // auto-updates as customers are added or removed via the RTDB watcher.
  // Counts EVERY customer in the scoped slice regardless of in-page
  // filters (the filtered list lives below as `list`).
  // Headline total + breakdown are derived from `list` (the displayed rows)
  // just below, so the top number always equals the last row number and the
  // breakdown never exceeds it — even while searching/filtering.

  const list = useMemo(() => {
    const all = Object.entries(scopedCustomers).map(([id, v]) => ({ id, ...v }))
    const q = search.trim().toLowerCase()
    return all.filter((c) => {
      if (q && !`${c.name || ''}`.toLowerCase().includes(q)) return false
      if (statusFilter !== 'all' && c.status !== statusFilter) return false
      if (methodFilter !== 'all') {
        const match = methodFilter === 'mail'     ? isTrueMail(c)
                    : methodFilter === 'nomethod' ? isNoMethod(c, currentInvoiceByCustomer[c.id])
                    : paymentMethodOf(c) === methodFilter
        if (!match) return false
      }
      if (minAmount && Number(c.monthlyAmount) < Number(minAmount)) return false
      return true
    }).sort(byLastName)
  }, [scopedCustomers, search, statusFilter, methodFilter, minAmount, currentInvoiceByCustomer])

  // Breakdown counts, scoped to the ACTIVE customers in the displayed list so
  // "set up" + "pending" always reconcile to "active" on every tab. Mail/check
  // customers (paymentMethod === 'mail') are SET UP for their method (pay by
  // check, no online PM needed), so they count as set up — never pending.
  // NOTE: isPaidOrSetUp() itself is intentionally left unchanged (keeps the row
  // badges and the "Text Unpaid" SMS list behaving exactly as before).
  const activeList   = list.filter((c) => (c.status || 'active') === 'active')
  const activeCount  = activeList.length
  const setUpCount   = activeList.filter((c) => isTrueMail(c) || isPaidOrSetUp(c, currentInvoiceByCustomer[c.id])).length
  const pendingCount = activeList.filter((c) => !isTrueMail(c) && !isPaidOrSetUp(c, currentInvoiceByCustomer[c.id])).length

  const pageTitle = customerType === 'program' && billingCycle === '1st'  ? 'YN AL Customers 1st'
                  : customerType === 'program' && billingCycle === '15th' ? 'YN AL Customers 15th'
                  : customerType === 'invoice' && billingCycle === '1st'  ? 'YN Customers 1st'
                  : customerType === 'invoice' && billingCycle === '15th' ? 'YN Customers 15th'
                  : customerType === 'invoice' && billingCycle === 'custom' ? 'YN Customers Custom'
                  : 'Customers'

  // "Unpaid" candidates for the bulk SMS modal — active customers in
  // this tab who don't have a saved Stripe payment method yet (proxy for
  // "needs to pay / set up payment"). Mail/check customers pay by check on
  // their own schedule and don't need an online-payment nudge, so they're
  // excluded from both the "Text Unpaid" count and the send list. Mark can
  // deselect anyone in the modal before confirming.
  const unpaidCandidates = useMemo(() => (
    Object.entries(scopedCustomers)
      .map(([id, c]) => ({ id, ...c }))
      .filter((c) => (c.status || 'active') === 'active' && c.paymentMethod !== 'mail' && !isPaidOrSetUp(c, currentInvoiceByCustomer[c.id]))
      .sort(byLastName)
  ), [scopedCustomers])

  // Admin records a mailed check / cash payment for a mail-or-check customer,
  // straight from the list — same server endpoint the detail page uses (flips
  // the invoice to paid, emails a slim receipt if there's an address on file,
  // no Stripe, no charge). The RTDB invoices watcher re-renders the row green.
  const onMarkMailPaid = async (inv, customer) => {
    if (!inv?.id || inv.__noCurrentCycle) return
    if (!confirm(`Record invoice #${inv.invoiceNumber} (${fmtMoney(inv.amount)}) for ${customer.name} as PAID by mail/check?`)) return
    setMarkFlash(null)
    setMarkingId(inv.id)
    try {
      const r = await markInvoicePaidManual(inv.id)
      if (!r?.ok) throw new Error(r?.error || 'Could not mark paid.')
    } catch (e) {
      setMarkFlash({ kind: 'err', msg: e?.message || 'Could not mark paid.' })
    } finally {
      setMarkingId(null)
    }
  }

  return (
    <div className="space-y-4">
      {/* Per-screen nav controls — Back walks one entry up the history
          stack; Home jumps straight to the Dashboard. Required on every
          screen per the app-wide nav rule. */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="btn-secondary text-xs"
          aria-label="Back">← Back</button>
        <Link to="/" className="btn-secondary text-xs" aria-label="Home">🏠 Home</Link>
      </div>

      <div className="flex justify-between items-center flex-wrap gap-2">
        <h1 className="text-2xl font-extrabold">{pageTitle}</h1>
        <div className="flex gap-2 flex-wrap">
          {/* "Send Signup Link" retired — first-time setup now lives on the
              invoice email's SET UP PAYMENT button + /pay chooser. */}
          <button
            type="button"
            onClick={() => setShowBulkSms(true)}
            disabled={unpaidCandidates.length === 0}
            className="btn-secondary disabled:opacity-50"
            title={unpaidCandidates.length === 0 ? 'No unpaid customers in this tab' : ''}>
            💬 Text Unpaid ({unpaidCandidates.length})
          </button>
          <Link to="/customers/new" className="btn-primary">+ Add Customer</Link>
        </div>
      </div>

      {/* Live customer count — matches dashboard Stat tile styling.
          Filled YesNet blue with gold label so the headline number is
          unmissable at a glance. */}
      <div className="card bg-brand-green text-white border-transparent">
        <div className="text-[10px] uppercase tracking-widest font-bold text-brand-gold">
          Total Customers
        </div>
        <div className="text-3xl font-extrabold mt-1 text-white">
          {list.length}
        </div>
        <div className="text-xs mt-0.5 text-white/70">
          {`${activeCount} active · ${setUpCount} set up · ${pendingCount} pending setup`}
        </div>
      </div>

      {/* In-page filters — billing-cycle dropdown removed because each
          tab already scopes to a single cycle. */}
      <div className="card">
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="sm:col-span-2">
            <label className="label">Search by name</label>
            <input className="input" placeholder="John Smith" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div>
            <label className="label">Status</label>
            <select className="input" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="label">Payment method</label>
            <select className="input" value={methodFilter} onChange={(e) => setMethodFilter(e.target.value)}>
              <option value="all">All</option>
              <option value="stripe">Stripe Auto-Draft</option>
              <option value="stripe_invoice">Stripe Invoice (Pay button)</option>
              <option value="card_auto">Card Auto-Pay</option>
              <option value="card_invoice">Card Invoice (Pay button)</option>
              <option value="mail">✉️ Mail / Check</option>
              <option value="nomethod">No Payment Method</option>
            </select>
          </div>
          <div>
            <label className="label">Min monthly amount ($)</label>
            <input type="number" min="0" className="input" value={minAmount} onChange={(e) => setMinAmount(e.target.value)} placeholder="0" />
          </div>
        </div>
      </div>

      {smsTarget ? (
        <SmsComposerModal
          customer={smsTarget.customer}
          customerId={smsTarget.id}
          onClose={() => setSmsTarget(null)} />
      ) : null}

      {showBulkSms ? (
        <BulkSmsModal
          tabLabel={pageTitle}
          candidates={unpaidCandidates}
          onClose={() => setShowBulkSms(false)} />
      ) : null}

      {markFlash ? (
        <div className={`card text-sm font-semibold ${markFlash.kind === 'err' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
          {markFlash.msg}
        </div>
      ) : null}

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500 bg-gray-50 border-b">
              <tr>
                <th className="text-right px-3 py-2 w-10">#</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-right px-4 py-2">Monthly</th>
                <th className="text-left px-4 py-2">Cycle</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Method</th>
                <th className="text-left px-4 py-2">Created</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">No customers match these filters.</td></tr>
              ) : list.map((c, i) => (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3" colSpan={7}>
                    <div className="flex items-center gap-3">
                      <span className="shrink-0 w-8 text-right text-xs font-semibold text-gray-400 tabular-nums">{i + 1}</span>
                      <div className="grid grid-cols-12 gap-2 items-center flex-1 min-w-0">
                      <span className="col-span-4 min-w-0 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSmsTarget({ id: c.id, customer: c }) }}
                          disabled={!c.phone}
                          aria-label={`Send SMS to ${c.name}`}
                          title={c.phone ? `Text ${c.name}` : 'No phone on file'}
                          className="shrink-0 w-8 h-8 rounded-full border border-gray-200 bg-white text-gray-600 hover:bg-brand-blue-deep hover:text-white hover:border-brand-blue-deep disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center text-base leading-none transition">
                          💬
                        </button>
                        <Link to={`/customers/${c.id}`} className="min-w-0 block flex-1">
                          <span className="font-bold truncate block">{c.name || '—'}</span>
                          <span className="text-xs text-gray-500 truncate block">{c.email || c.phone || ''}</span>
                        </Link>
                      </span>
                      <Link to={`/customers/${c.id}`} className="col-span-2 text-right font-bold">{fmtMoney(c.monthlyAmount)}</Link>
                      <Link to={`/customers/${c.id}`} className="col-span-1 text-xs">
                        {c.billingCycle === 'custom' ? (
                          <span className="flex flex-col leading-tight">
                            <span className="font-bold">custom</span>
                            <span className="text-gray-500 whitespace-nowrap">send {c.customSendDay ?? '—'} · due {c.customDueDay ?? '—'}</span>
                          </span>
                        ) : (c.billingCycle || '—')}
                      </Link>
                      <Link to={`/customers/${c.id}`} className="col-span-2"><span className={statusPillClass(c.status)}>{c.status || 'active'}</span></Link>
                      <div className="col-span-2 text-xs flex flex-col gap-1 min-w-0">
                        <Link to={`/customers/${c.id}`} className="flex flex-col gap-1 min-w-0">
                          {methodBadge(c, currentInvoiceByCustomer[c.id])}
                          {!isTrueMail(c) && invoiceStatusBadge(currentInvoiceByCustomer[c.id])}
                        </Link>
                        {isTrueMail(c) && (
                          <MailCheckPaidControl
                            inv={currentInvoiceByCustomer[c.id]}
                            marking={markingId === currentInvoiceByCustomer[c.id]?.id}
                            onMark={() => onMarkMailPaid(currentInvoiceByCustomer[c.id], c)} />
                        )}
                      </div>
                      <Link to={`/customers/${c.id}`} className="col-span-1 text-xs text-gray-400">{fmtDateTime(c.createdAt).split(',')[0]}</Link>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

// Payment-method badge — single pill per customer.
//
// Rule:
//   • Manual / Check customers: always show "Manual / Check" (their choice
//     does not involve a saved Stripe PM).
//   • Every other customer is on Stripe. Until stripePaymentMethodId is
//     written to Firebase by the signup flow, we don't know which method
//     they actually chose — so render a neutral "Stripe" pill and nothing
//     else. (No "Auto-Draft" promise, no "Pending Setup" warning that
//     might double up with another status.)
//   • Once stripePaymentMethodId is present, surface the chosen method:
//     Auto-Draft (ACH auto-pay), Bank on File (ACH pay-on-click), or
//     Card on File (any card-based rail).
//
// Because the parent reads /customers via Realtime Database watchCustomers,
// any change to stripePaymentMethodId in Firebase re-renders this badge
// automatically — no manual refresh needed.
// Current-cycle payment badge (display only):
//   Paid       → this cycle's invoice settled (payment_intent.succeeded)
//   Processing → charged and clearing (ACH takes 1-3 days; cards settle instantly)
//   Unpaid     → nothing charged this cycle (incl. no current-cycle invoice yet)
// Returns null only when there's no invoice on file at all, so stub cells stay
// clean. Voided/cancelled/refunded also render no badge.
function invoiceStatusBadge(inv) {
  if (!inv) return null
  const status = String(inv.status || '').toLowerCase()
  if (status === 'voided' || status === 'cancelled' || status === 'refunded') {
    return null
  }
  const state = paymentBadgeState(inv)
  if (state === 'paid') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-green-800 bg-green-50 border border-green-200 rounded px-2 py-0.5 self-start">
        <span>✓</span><span>Paid</span>
      </span>
    )
  }
  if (state === 'processing') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 self-start">
        <span>⏳</span><span>Processing</span>
      </span>
    )
  }
  if (status === 'overdue') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-white bg-red-600 border border-red-700 rounded px-2 py-0.5 self-start">
        <span>⚠</span><span>Overdue</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 font-extrabold text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 self-start">
      Unpaid
    </span>
  )
}

// TRUE mail customer — chose "Mailing Payment" on the pay page, which writes
// paymentMethod:'mail'. Only these get the "Mail / Check" label + the admin
// "Mark Check Paid" control. The legacy autoDraft:false → 'manual' fallback is
// NOT mail — those read "No Payment Method" (see isNoMethod).
function isTrueMail(c) {
  return c?.paymentMethod === 'mail'
}

// No payment method at all: not mail, nothing saved (no card/bank/auto-draft),
// and nothing paid. Covers the legacy autoDraft:false fallback AND stripe-rail
// customers who never finished setup — all read one label, "No Payment Method".
function isNoMethod(c, currentInvoice) {
  return c?.paymentMethod !== 'mail' && !c?.stripePaymentMethodId && !isPaidOrSetUp(c, currentInvoice)
}

// Inline paid/unpaid control for mail-or-check customers, shown in the list so
// received checks are obvious at a glance:
//   • unpaid current invoice → amber "✓ Mark Check Paid" button (click records it)
//   • paid                   → solid green "✓ Paid · date" pill
//   • no current-cycle invoice → muted hint (nothing to record yet)
function MailCheckPaidControl({ inv, marking, onMark }) {
  if (!inv || inv.__noCurrentCycle || !inv.id) {
    return <span className="text-[11px] text-gray-400 self-start">no invoice this cycle</span>
  }
  const paid = String(inv.status || '').toLowerCase() === 'paid'
  if (paid) {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-green-800 bg-green-100 border border-green-300 rounded px-2 py-0.5 self-start">
        <span>✓</span><span>Paid{inv.paidAt ? ` · ${fmtDateTime(inv.paidAt).split(',')[0]}` : ''}</span>
      </span>
    )
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); onMark() }}
      disabled={marking}
      title="Record a mailed check / cash payment for this invoice"
      className="inline-flex items-center gap-1 font-bold text-white bg-red-600 border border-red-700 rounded px-2 py-0.5 self-start hover:bg-red-700 disabled:opacity-50 transition">
      {marking ? '…' : '✓ Mark Check Paid'}
    </button>
  )
}

function methodBadge(c, currentInvoice) {
  // TRUE mail only — customer chose "Mailing Payment" on the pay page.
  if (c.paymentMethod === 'mail') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 self-start">
        <span>✉️</span><span>Mail / Check</span>
      </span>
    )
  }
  const m = paymentMethodOf(c)
  if (!c.stripePaymentMethodId) {
    // No saved PM. A one-time card payment saves no PM but is still paid.
    if (!isPaidOrSetUp(c, currentInvoice)) {
      // Nothing on file (manual fallback OR unfinished stripe setup) → one label.
      return (
        <span className="inline-flex items-center gap-1 font-bold text-gray-600 bg-gray-100 border border-gray-300 rounded px-2 py-0.5 self-start">
          No Payment Method
        </span>
      )
    }
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-green-800 bg-green-50 border border-green-200 rounded px-2 py-0.5 self-start">
        <span>✓</span><span>Paid (card)</span>
      </span>
    )
  }
  if (m === 'stripe_invoice') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-blue-800 bg-blue-50 border border-blue-200 rounded px-2 py-0.5 self-start">
        <span>✓</span><span>Bank on File</span>
      </span>
    )
  }
  if (m === 'card_auto' || m === 'card_invoice') {
    return (
      <span className="inline-flex items-center gap-1 font-extrabold text-pink-800 bg-pink-50 border border-pink-200 rounded px-2 py-0.5 self-start">
        <span>✓</span><span>Card on File</span>
      </span>
    )
  }
  // m === 'stripe' (the ACH auto-draft rail).
  return (
    <span className="inline-flex items-center gap-1 font-extrabold text-brand-green-deep bg-brand-mint border border-brand-green rounded px-2 py-0.5 self-start">
      <span>✓</span><span>Auto-Draft</span>
    </span>
  )
}
